import * as React from "react";
import { Button, Card, Grid, Sheet, Stack, Typography } from "@mui/joy";
import CampaignRoundedIcon from "@mui/icons-material/CampaignRounded";
import VolunteerActivismRoundedIcon from "@mui/icons-material/VolunteerActivismRounded";
import PhotoCameraRoundedIcon from "@mui/icons-material/PhotoCameraRounded";
import EmojiEventsRoundedIcon from "@mui/icons-material/EmojiEventsRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import { Link as RouterLink } from "react-router-dom";

interface QuickActionItem {
  title: string;
  description: string;
  cta: string;
  to: string;
  icon: React.ReactNode;
  variant?: "solid" | "soft" | "outlined";
}

const QUICK_ACTIONS: QuickActionItem[] = [
  {
    title: "Plan a new festival",
    description: "Set dates, seva focus, and logistics so the community can assemble in harmony.",
    cta: "Schedule event",
    to: "/events/new",
    icon: <EmojiEventsRoundedIcon fontSize="small" />,
    variant: "solid",
  },
  {
    title: "Broadcast an announcement",
    description: "Share uplifting news, seva invitations, or urgent service updates across channels.",
    cta: "Compose message",
    to: "/announcements/new",
    icon: <CampaignRoundedIcon fontSize="small" />,
    variant: "soft",
  },
  {
    title: "Record a heartfelt offering",
    description: "Capture donations promptly so devotees receive receipts and gratitude on time.",
    cta: "Log donation",
    to: "/donate",
    icon: <VolunteerActivismRoundedIcon fontSize="small" />,
    variant: "outlined",
  },
  {
    title: "Upload today’s darshan",
    description: "Bless everyone with new photos or videos from the altar and temple grounds.",
    cta: "Open media desk",
    to: "/media",
    icon: <PhotoCameraRoundedIcon fontSize="small" />,
    variant: "outlined",
  },
];

export function DashboardQuickActions() {
  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: "xl",
        p: { xs: 2.5, md: 3 },
        gap: 3,
        overflow: "hidden",
      }}
    >
      <Stack spacing={0.75}>
        <Typography level="title-md" sx={{ fontWeight: 700 }}>
          Stewardship quick actions
        </Typography>
        <Typography level="body-sm" sx={{ color: "text.secondary", maxWidth: 520 }}>
          These shortcuts lead you directly into the most common service flows so every devotional touchpoint stays current.
        </Typography>
      </Stack>

      <Grid container spacing={2} columns={{ xs: 12, md: 12 }}>
        {QUICK_ACTIONS.map((action) => (
          <Grid key={action.title} xs={12} md={6}>
            <Sheet
              variant="outlined"
              sx={{
                borderRadius: "lg",
                p: 2,
                height: "100%",
                display: "flex",
                flexDirection: "column",
                gap: 1.25,
                borderColor: "neutral.outlinedBorder",
                backgroundColor: "background.level1",
              }}
            >
              <Stack direction="row" spacing={1.2} alignItems="center">
                <Sheet
                  variant="soft"
                  color="primary"
                  sx={{
                    borderRadius: "lg",
                    width: 36,
                    height: 36,
                    display: "grid",
                    placeItems: "center",
                  }}
                >
                  {action.icon}
                </Sheet>
                <Typography level="title-sm" sx={{ fontWeight: 700 }}>
                  {action.title}
                </Typography>
              </Stack>
              <Typography level="body-sm" sx={{ color: "text.secondary", flexGrow: 1 }}>
                {action.description}
              </Typography>
              <Button
                component={RouterLink}
                to={action.to}
                variant={action.variant ?? "outlined"}
                color={action.variant === "solid" ? "primary" : "neutral"}
                endDecorator={<ArrowForwardRoundedIcon fontSize="small" />}
                sx={{ alignSelf: "flex-start", borderRadius: "lg" }}
              >
                {action.cta}
              </Button>
            </Sheet>
          </Grid>
        ))}
      </Grid>
    </Card>
  );
}

export default DashboardQuickActions;
